import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ShieldCheck, Clock, XCircle, CalendarCheck, Loader2, ScrollText, CalendarClock, ChevronLeft, ChevronRight, Star, Upload, Video, CheckCircle2, FileText } from "lucide-react";

const SLOTS = ["06:00 AM", "08:00 AM", "10:00 AM", "12:00 PM", "04:00 PM", "06:00 PM", "07:30 PM"];
const ID_TYPES = ["Aadhaar Card", "PAN Card", "Driving Licence", "Passport", "Voter ID"];
const BASE_LANGS = ["Sanskrit", "English", "Hindi"];
const STAGES = [
  { key: "submitted", label: "Submitted" },
  { key: "interview_scheduled", label: "Interview Scheduled" },
  { key: "in_process", label: "In Process" },
  { key: "approved", label: "Approved" },
];
const STATUS = {
  approved: { icon: ShieldCheck, cls: "text-emerald-600 bg-emerald-500/15", label: "Approved & onboarded" },
  verified: { icon: ShieldCheck, cls: "text-emerald-600 bg-emerald-500/15", label: "Verified & onboarded" },
  submitted: { icon: Clock, cls: "text-amber-600 bg-amber-500/15", label: "Submitted — under review" },
  pending: { icon: Clock, cls: "text-amber-600 bg-amber-500/15", label: "Submitted — under review" },
  interview_scheduled: { icon: Video, cls: "text-blue-600 bg-blue-500/15", label: "Interview scheduled" },
  in_process: { icon: Loader2, cls: "text-blue-600 bg-blue-500/15", label: "In process" },
  rejected: { icon: XCircle, cls: "text-destructive bg-destructive/15", label: "Not approved" },
};

const readFile = (file, maxMB, accept) =>
  new Promise((resolve, reject) => {
    if (!file) return resolve("");
    if (accept && !accept.some((t) => file.type === t)) return reject(new Error("Unsupported file type"));
    if (file.size > maxMB * 1024 * 1024) return reject(new Error(`File must be under ${maxMB}MB`));
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsDataURL(file);
  });

const Rating = ({ value, onChange, testid }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(Number(e.target.value))}
    className="h-9 w-[84px] rounded-lg border border-border bg-background px-2 text-sm">
    <option value={0}>–/5</option>
    {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}/5</option>)}
  </select>
);

export default function PurohitPortal() {
  const [me, setMe] = useState(null);
  const [mandirs, setMandirs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", age: "", gender: "", email: "", phone: "", address: "",
    id_type: ID_TYPES[0], id_number: "", education: "", gurukul: "", experience: 5,
    languages: [...BASE_LANGS.map((l) => ({ language: l, read: 0, write: 0, speak: 0 })), { language: "", read: 0, write: 0, speak: 0 }],
    bio: "", mandir_ids: [], service_types: ["worship"],
    biodata_pdf: "", profile_pic: "", id_card_pdf: "",
  });

  const load = () => api.get("/purohit/me").then((r) => setMe(r.data));
  useEffect(() => { load(); api.get("/mandirs").then((r) => setMandirs(r.data)); }, []);

  const toggle = (key, val) => setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));
  const setLang = (i, patch) => setForm((f) => ({ ...f, languages: f.languages.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));
  const onFile = async (field, file, maxMB, accept, label) => {
    try { const data = await readFile(file, maxMB, accept); setForm((f) => ({ ...f, [field]: data })); if (data) toast.success(`${label} attached`); }
    catch (e) { toast.error(e.message); }
  };

  const register = async () => {
    if (!form.name || !form.age || !form.gender || !form.email || !form.phone || !form.id_number || form.mandir_ids.length === 0) {
      toast.error("Please fill all required (*) fields and select at least one Mandir"); return;
    }
    if (!/.+@.+\..+/.test(form.email)) { toast.error("Please enter a valid email"); return; }
    if (form.service_types.length === 0) { toast.error("Select at least one service you offer"); return; }
    if (!form.biodata_pdf || !form.profile_pic || !form.id_card_pdf) { toast.error("Please upload biodata, profile picture and ID card"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form, age: Number(form.age) || null, experience: Number(form.experience) || 0,
        languages: form.languages.filter((l) => l.language.trim()),
      };
      await api.post("/purohit/register", payload);
      toast.success("Profile submitted for verification — check your email");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Registration failed"); }
    finally { setSaving(false); }
  };

  if (me === null) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  if (me.registered) {
    const st = STATUS[me.status] || STATUS.submitted;
    const bookable = me.status === "approved" || me.status === "verified";
    const rejected = me.status === "rejected";
    const activeIdx = STAGES.findIndex((s) => s.key === me.status);
    const curIdx = bookable ? STAGES.length - 1 : activeIdx;
    return (
      <div className="px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto py-12" data-testid="purohit-portal">
        <h1 className="font-serif text-3xl lg:text-4xl font-bold mb-2">Purohit Dashboard</h1>
        <p className="text-muted-foreground mb-8">Track your verification and manage bookings assigned to you.</p>

        <div className="rounded-2xl border border-border bg-card p-6 flex flex-col sm:flex-row gap-5 items-start">
          <img src={me.image} alt="" className="h-20 w-20 rounded-full object-cover" />
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-serif text-xl font-semibold">{me.name}</h2>
              <span data-testid="purohit-status-badge" className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1 ${st.cls}`}><st.icon className="h-3.5 w-3.5" /> {st.label}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{me.experience} yrs · {me.certifications}</p>
            {me.rating_count > 0 && <p className="text-sm mt-1 flex items-center gap-1 text-accent"><Star className="h-4 w-4 fill-accent" /> {me.rating} · {me.rating_count} reviews</p>}
            <p className="text-sm text-muted-foreground mt-2">{me.bio}</p>
          </div>
        </div>

        {/* Verification timeline */}
        {!rejected && (
          <div className="rounded-2xl border border-border bg-card p-6 mt-4" data-testid="verification-timeline">
            <h3 className="font-serif text-lg font-semibold mb-4">Verification progress</h3>
            <div className="flex items-center">
              {STAGES.map((s, i) => (
                <React.Fragment key={s.key}>
                  <div className="flex flex-col items-center text-center min-w-0">
                    <span className={`h-9 w-9 grid place-items-center rounded-full border-2 ${i <= curIdx ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>
                      {i < curIdx || (bookable && i === curIdx) ? <CheckCircle2 className="h-5 w-5" /> : i + 1}
                    </span>
                    <span className={`text-[11px] mt-2 ${i <= curIdx ? "text-primary font-medium" : "text-muted-foreground"}`}>{s.label}</span>
                  </div>
                  {i < STAGES.length - 1 && <div className={`h-0.5 flex-1 mx-1 ${i < curIdx ? "bg-primary" : "bg-border"}`} />}
                </React.Fragment>
              ))}
            </div>
            {me.status === "interview_scheduled" && me.interview && (
              <div className="mt-5 rounded-xl border border-blue-500/30 bg-blue-500/5 p-4" data-testid="interview-details">
                <p className="text-sm font-medium flex items-center gap-2"><Video className="h-4 w-4 text-blue-600" /> Your interview is scheduled</p>
                <p className="text-sm text-muted-foreground mt-1">{me.interview.scheduled_at}</p>
                <a href={me.interview.zoom_link} target="_blank" rel="noreferrer" data-testid="interview-zoom-link" className="inline-block mt-2 text-sm font-semibold text-primary hover:underline">Join Zoom meeting →</a>
              </div>
            )}
          </div>
        )}

        {me.status === "submitted" && <p className="mt-4 text-sm text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">Your profile is under review. We'll email you to schedule a short Zoom interview.</p>}
        {bookable && <p className="mt-4 text-sm text-emerald-600 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">You are approved and onboarded! Set your availability below to start receiving bookings.</p>}
        {rejected && <p className="mt-4 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-xl p-4">Your application was not approved at this time. Please contact our team for guidance.</p>}

        {bookable && <AvailabilityManager me={me} onSaved={load} />}

        <h3 className="font-serif text-xl font-semibold mt-10 mb-4 flex items-center gap-2"><CalendarCheck className="h-5 w-5 text-accent" /> Bookings assigned to you</h3>
        {me.bookings?.length ? (
          <div className="space-y-3">
            {me.bookings.map((b) => (
              <div key={b.id} data-testid={`purohit-booking-${b.id}`} className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-center justify-between gap-3">
                <div><p className="font-serif font-semibold">{b.service_name}</p><p className="text-xs text-muted-foreground">{b.mandir_name} · {b.date} · {b.time}</p></div>
                <div className="text-right"><p className="text-sm font-medium">{b.devotee?.name}</p><span className="text-xs capitalize text-muted-foreground">{b.status.replace("_", " ")}</span></div>
              </div>
            ))}
          </div>
        ) : <p className="text-muted-foreground text-sm">No bookings yet.</p>}
      </div>
    );
  }

  const FileField = ({ field, label, accept, maxMB, hint }) => (
    <div className="rounded-xl border border-dashed border-border p-4">
      <Label className="mb-1.5 block text-sm">{label}</Label>
      <input type="file" accept={accept.join(",")} data-testid={`upload-${field}`}
        onChange={(e) => onFile(field, e.target.files[0], maxMB, accept, label)} className="text-sm" />
      <p className="text-xs text-muted-foreground mt-1">{hint}{form[field] ? " · ✓ attached" : ""}</p>
    </div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto py-12" data-testid="purohit-portal">
      <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-2">Join as Purohit</p>
      <h1 className="font-serif text-3xl lg:text-4xl font-bold mb-2">Register as a Purohit / Priest</h1>
      <p className="text-muted-foreground mb-8">Complete your profile and submit for verification. Our team reviews every application with a short Zoom interview before onboarding.</p>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-6">
        {/* Personal */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-1"><Label className="mb-1.5 block">Full Name *</Label><Input data-testid="purohit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" /></div>
          <div><Label className="mb-1.5 block">Age *</Label><Input type="number" min="18" data-testid="purohit-age" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="rounded-xl" /></div>
          <div>
            <Label className="mb-1.5 block">Gender *</Label>
            <select data-testid="purohit-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm">
              <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="mb-1.5 block">Email *</Label><Input type="email" data-testid="purohit-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" placeholder="you@example.com" /></div>
          <div><Label className="mb-1.5 block">Phone number *</Label><Input data-testid="purohit-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl" placeholder="+91…" /></div>
        </div>
        <div><Label className="mb-1.5 block">Address</Label><Textarea data-testid="purohit-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-xl min-h-[70px]" placeholder="Your full address" /></div>

        {/* Identification */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block">Identification card *</Label>
            <select data-testid="purohit-id-type" value={form.id_type} onChange={(e) => setForm({ ...form, id_type: e.target.value })} className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm">
              {ID_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div><Label className="mb-1.5 block">ID number *</Label><Input data-testid="purohit-id-number" value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} className="rounded-xl" /></div>
        </div>

        {/* Qualifications */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="mb-1.5 block">Education qualification</Label><Input data-testid="purohit-education" value={form.education} onChange={(e) => setForm({ ...form, education: e.target.value })} className="rounded-xl" placeholder="e.g. Shastri, Acharya" /></div>
          <div><Label className="mb-1.5 block">Gurukul certification (if any)</Label><Input data-testid="purohit-gurukul" value={form.gurukul} onChange={(e) => setForm({ ...form, gurukul: e.target.value })} className="rounded-xl" placeholder="e.g. Sampurnanand Sanskrit Univ." /></div>
        </div>
        <div><Label className="mb-1.5 block">Years of experience</Label><Input type="number" min="0" data-testid="purohit-experience" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="rounded-xl max-w-[140px]" /></div>

        {/* Language proficiency */}
        <div>
          <Label className="mb-2 block">Language proficiency (rate 1–5)</Label>
          <div className="space-y-2">
            <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto] gap-3 text-xs text-muted-foreground px-1">
              <span>Language</span><span className="w-[140px]">Read</span><span className="w-[140px]">Write</span><span className="w-[140px]">Speak</span>
            </div>
            {form.languages.map((l, i) => (
              <div key={`lang-${i}-${l.language || "row"}`} data-testid={`lang-row-${i}`} className="grid sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-center rounded-xl border border-border p-3">
                {i < BASE_LANGS.length ? <span className="font-medium text-sm">{l.language}</span>
                  : <Input data-testid="lang-other-name" value={l.language} onChange={(e) => setLang(i, { language: e.target.value })} className="rounded-lg h-9" placeholder="Other language" />}
                <div className="w-[140px]"><Rating value={l.read} onChange={(v) => setLang(i, { read: v })} testid={`lang-read-${i}`} /></div>
                <div className="w-[140px]"><Rating value={l.write} onChange={(v) => setLang(i, { write: v })} testid={`lang-write-${i}`} /></div>
                <div className="w-[140px]"><Rating value={l.speak} onChange={(v) => setLang(i, { speak: v })} testid={`lang-speak-${i}`} /></div>
              </div>
            ))}
          </div>
        </div>

        <div><Label className="mb-1.5 block">About yourself</Label><Textarea data-testid="purohit-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="rounded-xl min-h-[90px]" placeholder="Your specialities, traditions and experience…" /></div>

        {/* Mandirs */}
        <div>
          <Label className="mb-2 block">Associate with Mandirs *</Label>
          <select data-testid="purohit-mandir-select" value="" onChange={(e) => { if (e.target.value) { toggle("mandir_ids", e.target.value); e.target.value = ""; } }}
            className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm">
            <option value="">Select a Mandir to associate…</option>
            {mandirs.filter((m) => !form.mandir_ids.includes(m.id)).map((m) => <option key={m.id} value={m.id}>{m.name} — {m.city}</option>)}
          </select>
          {form.mandir_ids.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {form.mandir_ids.map((id) => {
                const m = mandirs.find((x) => x.id === id);
                return (
                  <span key={id} data-testid={`mandir-chip-${id}`} className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary text-sm px-3 py-1">
                    {m?.name || id}
                    <button type="button" data-testid={`mandir-remove-${id}`} onClick={() => toggle("mandir_ids", id)} className="hover:text-destructive font-semibold leading-none">×</button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Services */}
        <div>
          <Label className="mb-2 block">Services you offer *</Label>
          <div className="flex gap-2">
            {["worship", "ritual"].map((t) => (
              <label key={t} className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm cursor-pointer capitalize ${form.service_types.includes(t) ? "border-primary bg-primary/5" : "border-border"}`}>
                <Checkbox checked={form.service_types.includes(t)} onCheckedChange={() => toggle("service_types", t)} data-testid={`purohit-service-${t}`} /> {t === "ritual" ? "Vedic Ritual" : "Worship"}
              </label>
            ))}
          </div>
        </div>

        {/* Uploads */}
        <div>
          <Label className="mb-2 block flex items-center gap-1.5"><Upload className="h-4 w-4 text-accent" /> Upload documents *</Label>
          <div className="grid sm:grid-cols-3 gap-3">
            <FileField field="biodata_pdf" label="Biodata (PDF) *" accept={["application/pdf"]} maxMB={5} hint="PDF, max 5MB" />
            <FileField field="profile_pic" label="Profile picture (JPG) *" accept={["image/jpeg"]} maxMB={2} hint="JPG, max 2MB" />
            <FileField field="id_card_pdf" label="ID card (PDF) *" accept={["application/pdf"]} maxMB={5} hint="PDF, max 5MB" />
          </div>
        </div>

        <Button onClick={register} disabled={saving} data-testid="purohit-register-btn" className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-11">
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</> : <>Submit for verification <ScrollText className="ml-2 h-4 w-4" /></>}
        </Button>
      </div>
    </div>
  );
}


function AvailabilityManager({ me, onSaved }) {
  const [avail, setAvail] = useState(me.availability || {});
  const [saving, setSaving] = useState(false);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [view, setView] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState("");

  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const monthLabel = view.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const firstDay = new Date(view.getFullYear(), view.getMonth(), 1).getDay();
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const toggleSlot = (d, s) => {
    const cur = avail[d] || [];
    const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
    const c = { ...avail };
    if (next.length) c[d] = next; else delete c[d];
    setAvail(c);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/purohit/availability", { availability: avail });
      toast.success("Availability saved");
      onSaved();
    } catch (e) { toast.error("Could not save availability"); }
    finally { setSaving(false); }
  };

  const totalSlots = Object.values(avail).reduce((s, arr) => s + arr.length, 0);

  return (
    <div className="mt-8 rounded-2xl border border-border bg-card p-6" data-testid="availability-manager">
      <h3 className="font-serif text-xl font-semibold flex items-center gap-2 mb-1"><CalendarClock className="h-5 w-5 text-accent" /> Manage your availability</h3>
      <p className="text-sm text-muted-foreground mb-5">Tap a day on the calendar, then tick the time slots you are free. Devotees only see open slots. If you set no days at all, all default slots stay open.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <button data-testid="cal-prev" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} className="h-8 w-8 grid place-items-center rounded-full border border-border hover:border-accent"><ChevronLeft className="h-4 w-4" /></button>
            <p className="font-serif font-semibold" data-testid="cal-month">{monthLabel}</p>
            <button data-testid="cal-next" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} className="h-8 w-8 grid place-items-center rounded-full border border-border hover:border-accent"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs text-muted-foreground mb-1">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <span key={d} className="py-1">{d}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, i) => {
              if (!day) return <span key={`empty-${i}`} />;
              const dObj = new Date(view.getFullYear(), view.getMonth(), day);
              const ds = fmt(dObj);
              const past = dObj < today;
              const has = (avail[ds] || []).length > 0;
              const isSel = selected === ds;
              return (
                <button key={ds} disabled={past} data-testid={`cal-day-${ds}`} onClick={() => setSelected(ds)}
                  className={`aspect-square rounded-lg text-sm relative transition-colors ${past ? "text-muted-foreground/30 cursor-not-allowed" : isSel ? "bg-primary text-primary-foreground" : has ? "bg-accent/20 text-accent-foreground border border-accent/50" : "hover:bg-secondary border border-transparent"}`}>
                  {day}
                  {has && !isSel && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-accent" />}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-3">{totalSlots} slot{totalSlots !== 1 ? "s" : ""} set across {Object.keys(avail).length} day{Object.keys(avail).length !== 1 ? "s" : ""}.</p>
        </div>

        <div>
          {selected ? (
            <div data-testid="cal-slots-panel">
              <p className="font-medium mb-3">{new Date(selected).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
              <div className="flex flex-wrap gap-2">
                {SLOTS.map((s) => (
                  <button key={s} onClick={() => toggleSlot(selected, s)} data-testid={`avail-slot-${selected}-${s.replace(/[:\s]/g, "")}`}
                    className={`px-3.5 py-2 rounded-full text-sm border ${(avail[selected] || []).includes(s) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-accent"}`}>{s}</button>
                ))}
              </div>
              {(avail[selected] || []).length > 0 && <button onClick={() => { const c = { ...avail }; delete c[selected]; setAvail(c); }} data-testid="clear-day" className="text-xs text-destructive mt-3 hover:underline">Clear this day</button>}
            </div>
          ) : (
            <div className="h-full grid place-items-center rounded-xl border border-dashed border-border p-6 text-center">
              <p className="text-sm text-muted-foreground">Select a day from the calendar to set its open time slots.</p>
            </div>
          )}
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="avail-save" className="mt-6 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
        {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Save availability"}
      </Button>
    </div>
  );
}
